import { useEffect, useState } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Loader2, CheckCircle, AlertCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import axios from 'axios';
import { trackPurchase } from '@/utils/metaPixel';

// Use window.location.origin to ensure we always call the correct backend
const API = `${window.location.origin}/api`;
const LOGO_URL = "/images/dea-wordmark.png";

export default function CheckoutSuccess() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState('checking'); // checking, success, error
  const [invitationCode, setInvitationCode] = useState(null);
  const [accessType, setAccessType] = useState(null);
  const [customerEmail, setCustomerEmail] = useState(null);
  const [error, setError] = useState(null);
  
  const sessionId = searchParams.get('session_id');
  const paymentIntentId = searchParams.get('payment_intent');

  useEffect(() => {
    const redirectStatus = searchParams.get('redirect_status');
    if (paymentIntentId && redirectStatus === 'failed') {
      // Redirect-based method (Klarna / Affirm / Amazon Pay) did not approve — send them back to pick another method.
      const back = new URLSearchParams(window.location.search);
      ['payment_intent', 'payment_intent_client_secret', 'redirect_status'].forEach((k) => back.delete(k));
      back.set('payment_failed', '1');
      navigate(`/checkout?${back.toString()}`, { replace: true });
      return;
    }
    if (paymentIntentId) {
      // New embedded Stripe Elements flow
      pollIntentStatus(paymentIntentId);
    } else if (sessionId) {
      // Legacy Stripe Checkout Session redirect flow
      pollPaymentStatus(sessionId);
    } else {
      setStatus('error');
      setError('No payment session found');
    }
  }, [sessionId, paymentIntentId]);

  const pollIntentStatus = async (piId, attempts = 0) => {
    const maxAttempts = 15;
    const pollInterval = 2000;

    if (attempts >= maxAttempts) {
      setStatus('error');
      setError('Payment verification timed out. Please contact support with your order details.');
      return;
    }

    try {
      const response = await axios.get(`${API}/payment/intent/status/${piId}`);
      const data = response.data;

      if (data.payment_status === 'paid') {
        setStatus('success');
        setInvitationCode(data.invitation_code);
        setAccessType(data.access_type);
        setCustomerEmail(data.customer_email);

        // Amount + product name resolution based on the access_type the server returned.
        // Defaults to the live PaymentIntent amount, then to $37 (monthly membership) as the
        // current public default. We intentionally do NOT fall back to $97 anymore —
        // Lifetime is retired and reporting a $97 Purchase to Meta would skew ad optimization.
        const accessTypeForTracking = data.access_type;
        const purchaseAmount =
          accessTypeForTracking === 'trial' || accessTypeForTracking === 'glow_up_trial' ? 9.00 :
          accessTypeForTracking === 'upgrade' ? 88.00 :
          accessTypeForTracking === 'monthly_direct' ? 37.00 :
          accessTypeForTracking === 'full' ? 97.00 :
          data.amount ? parseFloat(data.amount) : 37.00;
        const productName =
          accessTypeForTracking === 'trial' ? 'Dea 7-Day Trial' :
          accessTypeForTracking === 'glow_up_trial' ? 'Dea — 7-Day Trial' :
          accessTypeForTracking === 'upgrade' ? 'Dea Lifetime Upgrade' :
          accessTypeForTracking === 'monthly_direct' ? 'Dea Lifetime Access' :
          accessTypeForTracking === 'full' ? 'Dea Lifetime Access' :
          'Dea Lifetime Access';

        trackPurchase({
          value: purchaseAmount,
          currency: 'USD',
          content_name: productName,
          content_type: 'product',
          transaction_id: piId,
        });

        // For upgrade or auto-linked $97 purchases, redirect straight to chat
        if (!data.invitation_code && data.redirect_url) {
          setTimeout(() => navigate(data.redirect_url), 2000);
        }
        return;
      }

      // Still processing — keep polling
      setTimeout(() => pollIntentStatus(piId, attempts + 1), pollInterval);
    } catch (err) {
      console.error('Error checking PaymentIntent status:', err);
      if (attempts < maxAttempts - 1) {
        setTimeout(() => pollIntentStatus(piId, attempts + 1), pollInterval);
      } else {
        setStatus('error');
        setError('Error verifying payment. Please contact support.');
      }
    }
  };

  const pollPaymentStatus = async (sid, attempts = 0) => {
    const maxAttempts = 15;
    const pollInterval = 2000;

    if (attempts >= maxAttempts) {
      setStatus('error');
      setError('Payment verification timed out. Please contact support with your order details.');
      return;
    }

    try {
      const response = await axios.get(`${API}/payment/status/${sid}`);
      const data = response.data;

      if (data.payment_status === 'paid' && data.invitation_code) {
        setStatus('success');
        setInvitationCode(data.invitation_code);
        setAccessType(data.access_type);
        setCustomerEmail(data.customer_email);
        
        // Same resolution logic as the PaymentIntent path above — keep these two
        // branches in sync. See comment block in pollIntentStatus for rationale.
        const accessTypeForTracking = data.access_type;
        const purchaseAmount =
          accessTypeForTracking === 'trial' || accessTypeForTracking === 'glow_up_trial' ? 9.00 :
          accessTypeForTracking === 'upgrade' ? 88.00 :
          accessTypeForTracking === 'monthly_direct' ? 37.00 :
          accessTypeForTracking === 'full' ? 97.00 :
          data.amount ? parseFloat(data.amount) : 37.00;
        const productName =
          accessTypeForTracking === 'trial' ? 'Dea 7-Day Trial' :
          accessTypeForTracking === 'glow_up_trial' ? 'Dea — 7-Day Trial' :
          accessTypeForTracking === 'upgrade' ? 'Dea Lifetime Upgrade' :
          accessTypeForTracking === 'monthly_direct' ? 'Dea Lifetime Access' :
          accessTypeForTracking === 'full' ? 'Dea Lifetime Access' :
          'Dea Lifetime Access';
        
        trackPurchase({
          value: purchaseAmount,
          currency: 'USD',
          content_name: productName,
          content_type: 'product',
          transaction_id: sid
        });
        
        return;
      } else if (data.status === 'expired') {
        setStatus('error');
        setError('Payment session expired. Please try again.');
        return;
      }

      // Continue polling
      setTimeout(() => pollPaymentStatus(sid, attempts + 1), pollInterval);
    } catch (err) {
      console.error('Error checking payment status:', err);
      if (attempts < maxAttempts - 1) {
        setTimeout(() => pollPaymentStatus(sid, attempts + 1), pollInterval);
      } else {
        setStatus('error');
        setError('Error verifying payment. Please contact support.');
      }
    }
  };

  const handleContinue = () => {
    if (invitationCode) {
      navigate(`/signup?code=${invitationCode}`);
    }
  };

  return (
    <div className="min-h-screen bg-[#FFFCF7] flex flex-col items-center justify-center p-6">
      <Link to="/" className="mb-8">
        <img src={LOGO_URL} alt="Dea" className="h-12 w-auto" />
      </Link>

      {status === 'checking' && (
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          className="text-center"
        >
          <Loader2 className="h-16 w-16 text-[#87A6A7] animate-spin mx-auto mb-6" />
          <h1 className="font-serif text-3xl text-[#726158] mb-3">Verifying Your Payment</h1>
          <p className="text-[#AA9283]">Please wait while we confirm your purchase...</p>
        </motion.div>
      )}

      {status === 'success' && (
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          className="text-center max-w-md"
        >
          <div className="w-20 h-20 rounded-full bg-[#87A6A7]/20 flex items-center justify-center mx-auto mb-6">
            <CheckCircle className="h-10 w-10 text-[#87A6A7]" />
          </div>
          <h1 className="font-serif text-3xl text-[#726158] mb-3">Payment Successful!</h1>
          <p className="text-[#AA9283] mb-6">
            Thank you for your purchase! Your exclusive invitation code is ready.
          </p>
          
          {/* Invitation Code Display */}
          <div className="bg-[#EAD8C2]/30 rounded-xl p-4 mb-6 border border-[#EAD8C2]">
            <p className="text-[#AA9283] text-sm mb-2">Your Invitation Code</p>
            <p className="font-mono text-2xl text-[#87A6A7] font-bold tracking-wider">
              {invitationCode}
            </p>
            <p className="text-[#B1897C] text-xs mt-2">
              This code has been saved. You can use it to create your account.
            </p>
          </div>

          {/* Free Bonus Confirmation — full lifetime purchases only, not trial */}
          {accessType !== 'trial' && (
            <div
              className="rounded-xl mb-6 text-left"
              style={{
                backgroundColor: '#D9B5AD',
                border: '1px solid #B8956A',
                padding: '12px 16px'
              }}
              data-testid="bonus-delivery-confirmation"
            >
              <p style={{ color: '#3D3229', fontSize: '13px', lineHeight: 1.55, margin: 0 }}>
                <span style={{ color: '#B8956A', fontWeight: 600 }}>✓ </span>
                Your free 2026 Digital Ambition Planner will be delivered to{' '}
                {customerEmail ? (
                  <span style={{ fontWeight: 600 }}>{customerEmail}</span>
                ) : (
                  'the email you use at signup'
                )}
                . Check your inbox.
              </p>
            </div>
          )}

          <Button
            onClick={handleContinue}
            className="btn-gold w-full py-6 text-lg"
            data-testid="continue-to-signup"
          >
            Continue to Sign Up
          </Button>
        </motion.div>
      )}

      {status === 'error' && (
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          className="text-center max-w-md"
        >
          <div className="w-20 h-20 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-6">
            <AlertCircle className="h-10 w-10 text-red-500" />
          </div>
          <h1 className="font-serif text-3xl text-[#726158] mb-3">Something Went Wrong</h1>
          <p className="text-[#AA9283] mb-6">{error}</p>
          
          <div className="space-y-3">
            <Link to="/checkout?plan=lifetime">
              <Button className="btn-gold w-full py-4">
                Try Again
              </Button>
            </Link>
            <Link to="/">
              <Button variant="outline" className="w-full py-4 border-[#EAD8C2] text-[#AA9283]">
                Return Home
              </Button>
            </Link>
          </div>
        </motion.div>
      )}
    </div>
  );
}
